"use client";

import React from "react";
import { ShoppingBag, Loader, UserPlus } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
function RegistrationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-accent/20 bg-accent/5 text-center animate-in fade-in slide-in-from-bottom-4 duration-700">
        <h3 className="text-2xl font-bold text-foreground mb-2">Welcome to the Atelier</h3>
        <p className="text-muted-foreground mb-6">{successMessage || "Your account has been created successfully."}</p>
        <Button onClick={resetForm} variant="outline">
          Register Another Account
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <FormField control={control} name="first_name" rules={{
        required: "First name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="uppercase tracking-widest text-xs font-semibold">First Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane" {...field} className="border-0 border-b border-border focus-visible:ring-0 focus-visible:border-accent transition-colors px-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="last_name" rules={{
        required: "Last name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="uppercase tracking-widest text-xs font-semibold">Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Doe" {...field} className="border-0 border-b border-border focus-visible:ring-0 focus-visible:border-accent transition-colors px-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="space-y-6">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="uppercase tracking-widest text-xs font-semibold">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" {...field} className="border-0 border-b border-border focus-visible:ring-0 focus-visible:border-accent transition-colors px-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="username" rules={{
        required: "Username is required",
        minLength: {
          value: 4,
          message: "Username must be at least 4 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="uppercase tracking-widest text-xs font-semibold">Username</FormLabel>
              <FormControl>
                <Input placeholder="janedoe_style" {...field} className="border-0 border-b border-border focus-visible:ring-0 focus-visible:border-accent transition-colors px-0" />
              </FormControl>
              <FormDescription className="text-[10px] uppercase tracking-tight">This will be your public display name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="password" rules={{
        required: "Password is required",
        minLength: {
          value: 8,
          message: "Password must be at least 8 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="uppercase tracking-widest text-xs font-semibold">Password</FormLabel>
              <FormControl>
                <Input type="password" placeholder="••••••••" {...field} className="border-0 border-b border-border focus-visible:ring-0 focus-visible:border-accent transition-colors px-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="mt-6 p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full mt-10 bg-primary text-primary-foreground hover:bg-primary/90 py-6 text-base uppercase tracking-widest font-bold transition-transform active:translate-x-1 active:translate-y-1">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Creating Account...
          </> : <>
            <UserPlus className="mr-2 size-5" />
            Join the Atelier
          </>}
      </Button>
    </Form>;
}
export default function WooCommerceMyAccount() {
  return <section id="woocommercemyaccount" className="bg-background py-16 md:py-24 min-h-screen">
      <div className="container mx-auto px-4 max-w-6xl">
        
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-12 gap-6 border-b border-border pb-8">
          <div>
            <h1 className="font-default text-4xl font-bold text-foreground tracking-tight">My Account</h1>
            <p className="text-muted-foreground mt-2">Manage your orders, addresses, and account settings.</p>
          </div>
          <Button variant="outline" asChild className="shrink-0 shadow-sm">
            <Link to="/ShopPage">
              <ShoppingBag className="mr-2 size-4" />
              Continue Shopping
            </Link>
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="sticky top-24">
              <h2 className="text-2xl font-bold mb-4 uppercase tracking-tight">Create an Account</h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Join Lush Fashion to track your orders, manage your wardrobe wishlist, and receive exclusive access to our seasonal collections.
              </p>
              
              <FormProvider formId="register_form" sectionName="WooCommerceMyAccount" defaultValues={{
              first_name: "",
              last_name: "",
              email: "",
              username: "",
              password: ""
            }} successMessage="Welcome to Lush Fashion! Your account has been successfully created.">
                <RegistrationForm />
              </FormProvider>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="w-full prose prose-neutral max-w-none dark:prose-invert">
              <div dangerouslySetInnerHTML={{
              __html: '[woocommerce_my_account]'
            }} />
            </div>
          </div>
        </div>
        
      </div>
    </section>;
}